/**
 * Pure helpers for the Trends page (demo: revenue / expenses / NOI, full-year
 * tiles, season shading, late-cycle check). No I/O; the inputs are the
 * /api/trends, /api/trends/economy and /api/trends/cycles responses the page
 * already loads.
 */
export type Vec = Array<number | null>;

/** Expenses = revenue − NOI (everything above NOI: departmental, undistributed, fees, fixed). */
export const expenses = (rev: Vec, noi: Vec): Vec => rev.map((r, i) => (r === null || noi[i] == null ? null : r - noi[i]!));

/**
 * Peak and low season from history: average occupancy per calendar month over
 * all complete observations; the top 3 months are peak, the bottom 3 low.
 * Needs at least 12 months with a value.
 */
export function seasons(months: string[], occupancy: Vec): { peak: number[]; low: number[] } | null {
  const sum = Array(12).fill(0), n = Array(12).fill(0);
  months.forEach((m, i) => { const v = occupancy[i]; if (v != null) { const k = Number(m.slice(5, 7)) - 1; sum[k] += v; n[k]++; } });
  if (n.filter((c) => c > 0).length < 12) return null;
  const avg = sum.map((s, k) => ({ k: k + 1, v: s / n[k] })).sort((a, b) => b.v - a.v);
  return { peak: avg.slice(0, 3).map((a) => a.k).sort((a, b) => a - b), low: avg.slice(-3).map((a) => a.k).sort((a, b) => a - b) };
}

export interface YearCompare { label: string; revenue: number; noi: number | null; lyRevenue: number | null; lyNoi: number | null }

/** Last complete year vs the year before, and this year-to-date vs the same months last year (only months both years reported). */
export function fullYearTiles(months: string[], rev: Vec, noi: Vec): { lastYear: YearCompare | null; ytd: YearCompare | null } {
  const byM = new Map(months.map((m, i) => [m, i]));
  const sumFor = (list: string[], v: Vec) => {
    let t = 0;
    for (const m of list) { const i = byM.get(m); if (i === undefined || v[i] == null) return null; t += v[i]!; }
    return list.length ? t : null;
  };
  const yearMonths = (y: number, upTo = 12) => Array.from({ length: upTo }, (_, k) => `${y}-${String(k + 1).padStart(2, "0")}`);
  const last = months.at(-1);
  if (!last) return { lastYear: null, ytd: null };
  const curY = Number(last.slice(0, 4)), curM = Number(last.slice(5, 7));
  const fullY = curM === 12 ? curY : curY - 1;
  const mk = (label: string, a: string[], b: string[]): YearCompare | null => {
    const r = sumFor(a, rev);
    return r === null ? null : { label, revenue: r, noi: sumFor(a, noi), lyRevenue: sumFor(b, rev), lyNoi: sumFor(b, noi) };
  };
  return {
    lastYear: mk(String(fullY), yearMonths(fullY), yearMonths(fullY - 1)),
    ytd: curM === 12 ? null : mk(`${curY} through ${new Date(last + "-01T12:00:00Z").toLocaleDateString("en-US", { month: "short", timeZone: "UTC" })}`,
      yearMonths(curY, curM), yearMonths(curY - 1, curM)),
  };
}

const chg = (a: number, b: number | null) => (b ? ((a - b) / Math.abs(b)) * 100 : null);

/** One factual paragraph from the tiles — no reasons invented. */
export function trendsNarrative(t: ReturnType<typeof fullYearTiles>, fmt: (n: number) => string, pctFmt: (n: number) => string): string {
  const parts: string[] = [];
  const move = (c: number | null) => (c === null ? "" : Math.abs(c) < 0.05 ? ", flat" : `, ${c > 0 ? "up" : "down"} ${pctFmt(Math.abs(c))}`);
  const say = (c: YearCompare, lead: string, vs: string) => {
    const r = chg(c.revenue, c.lyRevenue);
    let s = `${lead} revenue was ${fmt(c.revenue)}${move(r)}${r === null ? "" : ` ${vs}`}`;
    if (c.noi !== null) s += `; NOI was ${fmt(c.noi)} (${pctFmt((c.noi / c.revenue) * 100)} margin)${move(chg(c.noi, c.lyNoi))}`;
    parts.push(s + ".");
  };
  if (t.lastYear) say(t.lastYear, `In ${t.lastYear.label}`, "on the year before");
  if (t.ytd) say(t.ytd, `So far in ${t.ytd.label.split(" ")[0]},`, "on the same months last year");
  return parts.join(" ");
}

export interface CycleNow { monthsSinceTrough: number | null; vsPeakPct: number | null; expansionMonths: number | null; medianExpansionMonths: number | null; lateCycle: boolean | null; inDownturn: boolean }

/** Demo "Late-cycle check": each line says what it measures, the reading, and whether it points late-cycle. */
export function lateCycleRows(industry: CycleNow | null, own: CycleNow | null, econ: Array<{ label: string; signal: "watch" | "ok" | null }>) {
  const rows: Array<{ check: string; reading: string; late: boolean | null }> = [];
  if (industry) {
    rows.push({ check: "U.S. hotel expansion length vs past expansions",
      reading: industry.inDownturn ? "In a downturn" : `${industry.expansionMonths ?? "—"} months vs ${industry.medianExpansionMonths ?? "—"} median`,
      late: industry.inDownturn ? null : industry.lateCycle });
    rows.push({ check: "U.S. hotel industry vs its last peak",
      reading: industry.vsPeakPct === null ? "—" : industry.vsPeakPct >= -0.5 ? "At or near the peak" : `${Math.abs(industry.vsPeakPct).toFixed(1)}% below`,
      late: industry.vsPeakPct === null ? null : industry.vsPeakPct >= -0.5 });
  }
  if (own) rows.push({ check: "Our T12 RevPAR vs its peak",
    reading: own.inDownturn ? "In a downturn" : own.vsPeakPct === null ? "—" : own.vsPeakPct >= -0.5 ? "At or near the peak" : `${Math.abs(own.vsPeakPct).toFixed(1)}% below`,
    late: own.inDownturn ? null : own.vsPeakPct === null ? null : own.vsPeakPct >= -0.5 });
  for (const e of econ) if (e.signal) rows.push({ check: e.label, reading: e.signal === "watch" ? "Watch" : "OK", late: e.signal === "watch" });
  return rows;
}

const median = (a: number[]) => { if (!a.length) return null; const b = [...a].sort((x, y) => x - y); const m = Math.floor(b.length / 2); return b.length % 2 ? b[m]! : (b[m - 1]! + b[m]!) / 2; };

/** Demo "What the cycles say about timing a sale": what past cycles did, stated as history, not a forecast. */
export function saleTimingNote(downturns: Array<{ monthsDown: number; monthsToRecover: number | null; depthPct: number }>, now: CycleNow): string[] {
  const down = median(downturns.map((d) => d.monthsDown));
  const rec = median(downturns.filter((d) => d.monthsToRecover !== null).map((d) => d.monthsToRecover!));
  const depth = median(downturns.map((d) => Math.abs(d.depthPct)));
  const out: string[] = [];
  if (downturns.length) out.push(`Since 2000 there have been ${downturns.length} U.S. hotel downturns. The typical one fell ${depth!.toFixed(1)}% over ${down} months${rec !== null ? ` and took ${rec} months to get back to the old peak` : ""}.`);
  if (now.inDownturn) out.push("The industry is in a downturn now; past ones ended in recovery, but buyers usually price in the trough while it lasts.");
  else if (now.expansionMonths !== null && now.medianExpansionMonths !== null) {
    out.push(now.expansionMonths >= now.medianExpansionMonths
      ? `This expansion is ${now.expansionMonths} months old, longer than the ${now.medianExpansionMonths}-month median.`
      : `This expansion is ${now.expansionMonths} months old, shorter than the ${now.medianExpansionMonths}-month median; past expansions have usually run longer than this one so far.`);
    if (down !== null && rec !== null)
      out.push(`If the next downturn looks like the typical past one, an owner who holds through it waits about ${down + rec} months from the peak until the industry is back to it.`);
  }
  return out;
}

export interface Forecast { months: string[]; totalRevenue: Vec; noi: Vec }

/**
 * Demo forecast line: the reforecast for the rest of this year, drawn dashed
 * and joined to the last actual point. Monthly: the forecast months are added
 * to the axis. Annual: one dashed segment from last year to this year's
 * projected total (actual so far + reforecast). Also returns the projected
 * full-year totals for the tiles. Nothing when there's no forecast.
 */
export function withForecast(grain: "month" | "year", x: string[], rev: Vec, noi: Vec, f: Forecast | null, label = (m: string) => m.slice(2).replace("-", "/")) {
  const none = { x, rev, noi, fRev: null as Vec | null, fNoi: null as Vec | null, projected: null as null | { year: string; revenue: number | null; noi: number | null } };
  if (!f || !f.months.length || !x.length) return none;
  const year = f.months[0]!.slice(0, 4);
  const sum = (v: Vec) => (v.every((n) => n !== null) ? (v as number[]).reduce((a, b) => a + b, 0) : null);
  const last = x.length - 1;
  if (grain === "month") {
    const fx = f.months.map(label);
    const pad = (v: Vec): Vec => [...v, ...fx.map(() => null)];
    const join = (actual: Vec, fc: Vec): Vec => [...x.map((_, i) => (i === last ? actual[last] ?? null : null)), ...fc];
    return { x: [...x, ...fx], rev: pad(rev), noi: pad(noi), fRev: join(rev, f.totalRevenue), fNoi: noi.some((v) => v !== null) ? join(noi, f.noi) : null, projected: null };
  }
  // Annual: the last point must be this (partial) year.
  if (!x[last]!.startsWith(year)) return none;
  const fr = sum(f.totalRevenue), fn = sum(f.noi);
  const projRev = rev[last] != null && fr !== null ? rev[last]! + fr : null;
  const projNoi = noi[last] != null && fn !== null ? noi[last]! + fn : null;
  const seg = (prev: number | null, end: number | null): Vec | null => (end === null ? null : x.map((_, i) => (i === last ? end : i === last - 1 ? prev : null)));
  return { x, rev, noi, fRev: seg(last ? rev[last - 1] ?? null : null, projRev), fNoi: seg(last ? noi[last - 1] ?? null : null, projNoi),
    projected: { year, revenue: projRev, noi: projNoi } };
}
